import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

import MessagingChannelRowActions from '~components/Messaging/MessagingChannelRowActions';
import MutedConversationIcon from '~components/Messaging/MutedConversationIcon';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import { MessagingChannel } from '~hooks/useMessaging/models';
import MessagingAvatar from '~pages/Messages/components/MessagingAvatar';
import {
  messagingChannelPreview,
  messagingLastActivityLabel,
} from '~pages/Messages/messagingChannelPreview';
import { Nullable } from '~types/Nullable';

// Wide enough to mark the selected row without shifting its content.
const SELECTED_ROW_ACCENT_WIDTH_IN_PX = 3;

// Centers the 28px actions button on the preview line, at the row's right padding.
const ROW_ACTIONS_BUTTON_INSET_IN_PX = { right: 8, top: 28 };
// Keeps the preview's ellipsis clear of the actions button that sits over its end.
const PREVIEW_ACTIONS_GUTTER_IN_PX = 28;

type OrderJobConversationRowProps = {
  avatarColorKey: string;
  channel: MessagingChannel;
  driverName: string;
  isSelected: boolean;
  jobLabel: string;
  onSelect: (channel: MessagingChannel) => void;
  viewerUserId: Nullable<string>;
};

export default function OrderJobConversationRow({
  avatarColorKey,
  channel,
  driverName,
  isSelected,
  jobLabel,
  onSelect,
  viewerUserId,
}: OrderJobConversationRowProps) {
  const theme = useTheme();

  return (
    <MessagingChannelRowActions
      buttonInset={ROW_ACTIONS_BUTTON_INSET_IN_PX}
      channel={channel}
    >
      <ButtonBase
        aria-current={isSelected}
        data-test-id="order-job-conversation-row"
        onClick={() => onSelect(channel)}
        sx={{
          alignItems: 'flex-start',
          borderBottom: `solid 1px ${theme.semantic.divider}`,
          display: 'flex',
          gap: 1.25,
          position: 'relative',
          px: 1.5,
          py: 1.5,
          textAlign: 'left',
          width: '100%',
          '&:hover': { backgroundColor: theme.semantic.listItemHoverBg },
          ...(isSelected
            ? {
                backgroundColor: theme.semantic.listItemHoverBg,
                '&::before': {
                  backgroundColor: theme.brandV2.colors.treadYellow,
                  bottom: 0,
                  content: '""',
                  left: 0,
                  position: 'absolute',
                  top: 0,
                  width: `${SELECTED_ROW_ACCENT_WIDTH_IN_PX}px`,
                },
              }
            : {}),
        }}
      >
        <MessagingAvatar colorKey={avatarColorKey} name={driverName} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 1 }}>
            <Typography noWrap sx={{ flex: 1, fontSize: '13px', fontWeight: 600 }}>
              {driverName}
            </Typography>

            <Typography
              sx={{
                color: theme.semantic.textSecondary,
                flexShrink: 0,
                fontSize: '11px',
              }}
            >
              {messagingLastActivityLabel(channel.lastMessageAt)}
            </Typography>

            {channel.muted && <MutedConversationIcon />}
            <UnreadCountBadge count={channel.unreadCount} isMuted={channel.muted} />
          </Box>

          <Typography
            noWrap
            sx={{
              color: theme.semantic.textSecondary,
              fontSize: '12px',
              mt: 0.25,
              pr: `${PREVIEW_ACTIONS_GUTTER_IN_PX}px`,
              ...(channel.lastMessage?.isDeleted ? { fontStyle: 'italic' } : {}),
            }}
          >
            {messagingChannelPreview(channel.lastMessage, viewerUserId)}
          </Typography>

          {jobLabel && (
            <Typography
              component="span"
              sx={{
                bgcolor: alpha(theme.brandV2.colors.treadGreen, 0.14),
                borderRadius: '999px',
                color: theme.brandV2.colors.treadGreen,
                display: 'inline-block',
                fontSize: '10px',
                fontWeight: 700,
                mt: 0.75,
                px: 1,
                py: 0.25,
              }}
            >
              {jobLabel}
            </Typography>
          )}
        </Box>
      </ButtonBase>
    </MessagingChannelRowActions>
  );
}
